import { useState } from 'react';
import { styled } from '@mui/material/styles';

import type { Neighborhood } from '../../shared/cityFile';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { DistanceSlider, ModeToggle, Segmented } from './controls';
import { countSentence, shortPlace } from './estimate';
import type { AddressOrigin, Home, LocateStatus, OriginKind } from './home';
import HomePicker from './HomePicker';
import { TextButton } from './styles';
import type { TravelMode } from './walkshed';

/**
 * The filters' distance section (contract §11), between the weekday row and
 * "I want to be a…": where from (Home | Current location), the home itself,
 * how (Walk | Bike | Transit), and how far, with the live count.
 *
 * With no home and no position there is nothing to measure from, so the
 * section says so, offers the picker, and holds the rest disabled.
 */

const Note = styled('p')({
  margin: '0 0 12px',
  fontFamily: FONTS.body,
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.4,
  color: 'rgba(243,238,231,0.75)',
});

const HomeLine = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  gap: 10,
  margin: '12px 0 14px',
  fontFamily: FONTS.body,
  fontSize: 13.5,
  fontWeight: 600,
  color: BRAND.parchment,
  '& > span': { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
});

const Block = styled('div')({ marginTop: 14 });

const Count = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 10,
  marginTop: 16,
  fontFamily: FONTS.body,
  fontSize: 13,
  fontWeight: 700,
  color: BRAND.parchment,
  fontVariantNumeric: 'tabular-nums',
});

interface Props {
  neighborhoods: Neighborhood[];
  home: Home | null;
  onSetHome: (home: Omit<Home, 'mode'>) => void;
  origin: OriginKind;
  onOrigin: (origin: OriginKind) => void;
  /** A place searched in the map's popover this session: offered as a third origin while it exists. */
  address?: AddressOrigin | null;
  /** Current location: whether a position has arrived, or is being asked for. */
  locate: LocateStatus;
  canLocate: boolean;
  onLocate: () => void;
  mode: TravelMode;
  onMode: (mode: TravelMode) => void;
  withinMinutes: number | null;
  onWithin: (minutes: number | null) => void;
  /** `facetCounts().withinMinutes`: per step, and `any`. */
  counts: Record<number | 'any', number>;
}

export default function DistanceSection({
  neighborhoods,
  home,
  onSetHome,
  origin,
  onOrigin,
  address = null,
  locate,
  canLocate,
  onLocate,
  mode,
  onMode,
  withinMinutes,
  onWithin,
  counts,
}: Props) {
  const [editing, setEditing] = useState(false);
  const located = locate === 'ready';
  const measurable = Boolean(home) || located || Boolean(address);
  const showPicker = !home || editing;
  const count = withinMinutes == null ? counts.any : (counts[withinMinutes] ?? 0);

  return (
    <div>
      {!measurable && <Note>Set a home location to filter by travel time.</Note>}

      <Segmented<OriginKind>
        label="Measure from"
        value={origin === 'current' && located ? 'current' : origin === 'address' && address ? 'address' : 'home'}
        disabled={!measurable}
        options={[
          { value: 'home', label: 'Home', disabled: !home },
          {
            value: 'current',
            label: 'Current location',
            disabled: !located,
            title: located ? undefined : 'Use your current location first',
          },
          ...(address ? [{ value: 'address' as const, label: shortPlace(address.label), title: address.label }] : []),
        ]}
        onChange={onOrigin}
      />

      {home && !editing && (
        <HomeLine>
          <span title={home.label}>🏠 {home.label}</span>
          <TextButton type="button" onClick={() => setEditing(true)}>
            Change
          </TextButton>
        </HomeLine>
      )}
      {showPicker && (
        <Block>
          <HomePicker
            neighborhoods={neighborhoods}
            home={home}
            onSetHome={(h) => {
              onSetHome(h);
              setEditing(false);
            }}
            onCancel={home ? () => setEditing(false) : undefined}
            onUseCurrent={canLocate && !located ? onLocate : undefined}
            locating={locate === 'locating'}
          />
        </Block>
      )}
      {home && !editing && canLocate && !located && (
        <TextButton type="button" onClick={onLocate} disabled={locate === 'locating'} style={{ marginBottom: 4 }}>
          {locate === 'locating' ? 'Finding you…' : 'Use my current location'}
        </TextButton>
      )}

      <Block>
        <ModeToggle mode={mode} onChange={onMode} disabled={!measurable} />
      </Block>

      <Count aria-live="polite">
        <span>{measurable ? countSentence(count, withinMinutes) : 'Travel time'}</span>
      </Count>
      <DistanceSlider minutes={withinMinutes} onChange={onWithin} disabled={!measurable} />
    </div>
  );
}
